import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from "react-native";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";

import { v4 as uuidv4 } from "uuid";

import BlockDescriptionView from "../../Block/BlockDescriptionView";
import Serie from "../../Firestore/Serie";

import SerieRowView from "../../Serie/SerieRowView";
import WorkoutDescriptionView from "../../Workout/WorkoutDescriptionView";
import GlobalStyles from "../../../Resources/Styles";
import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import CustomAlert from "../../Components/CustomAlert";
import Tooltip from "../../Components/Tooltip";
import FloatingMenu from "../../Components/FloatingMenu";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import { observer, Observer } from "mobx-react";
import { reaction } from "mobx";
import { useTranslation } from "react-i18next";
import { useScreenDimensions, useDevice } from "../../_layout";
import { ScrollView } from "react-native-web";

import BackButton from "../../Components/BackButton";
import EditWorkoutView from "../../Workout/EditWorkoutView";
import InfoView from "../../Block/InfoView";
import { formatNumber } from "../../utils/numberUtils";

import ImageMessageManager from "../../Classes/ImageMessageManager";
import { AntDesign, Ionicons } from "@expo/vector-icons";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Octicons } from "@expo/vector-icons";
import { Feather } from "@expo/vector-icons";
import { Entypo } from "@expo/vector-icons";
import { format } from "date-fns";
import AddToCalendarView from "../../Block/AddToCalendarView";
import WorkoutRowView from "../../Workout/WorkoutRowView";
import AddRow from "../../Components/AddRow";
import { ProtocolTitles, UISections } from "../../../Resources/Constants";

import SearchBar from "../../Components/SearchBar";
import PredefinedAttributeLayoutView from "../../Exercise/PredefinedAttributeLayoutView";
import PredefinedExerciseLayoutView from "../../Exercise/PredefinedExerciseLayoutView";
import Workout from "../../Firestore/Workout";
import Exercise from "../../Firestore/Exercise";
import Attribute from "../../Firestore/Attribute";

import DeleteBlockView from "../../Block/DeleteBlockView";
import CopyBlockToClient from "../../Block/CopyBlockToClient";
import AddBlockView from "../../Block/AddBlockView";

import BlockDashboard from "../../Block/BlockDashboard";
import BlockView from "../../Block/shared/BlockView";
import TutorialLinkSection from "../../Components/TutorialLinkSection";
import SendFeedback from "../../Components/SendFeedback";
import BlockViewUpdateModal from "./BlockViewUpdateModal";
import BlockViewRevertModal from "../../Block/shared/BlockViewRevertModal";
import BlocksList from "./BlocksList/BlocksList";
import WorkoutsDashboardTabBar from "../../Components/WorkoutsDashboardTabBar";
import { useToast } from "../../contexts/ToastContext";
import {
  getBlockTutorialVisibility,
  shouldReduceBlockViewHeaderGap,
  getBlockHeaderBottomMargin,
} from "./blockViewLayoutUtils";

const ClientPlanification = observer(({ client }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  const isLegacyCoach = Boolean(client?.coach?.isLegacyCoachForBlockView?.());
  const shouldShowBlockViewUpdateModal = Boolean(
    client?.coach?.shouldShowBlockViewUpdateModal?.(
      UISections.BLOCK_VIEW_UPDATE_V1,
    ),
  );
  const coachUseLegacyPlanView = Boolean(client?.coach?.useLegacyPlanView);
  const coachLegacyPlanViewSelection = coachUseLegacyPlanView
    ? client?.coach?.useLegacyPlanViewSelection
    : undefined;
  const hasDismissedBlockViewBanner = Boolean(
    client?.coach?.UISections?.[UISections.BLOCK_VIEW_TUTORIAL_BANNER_V1],
  );
  const blockViewTutorialUrlPath = "/program/create-block";

  const [showingBlockViewUpdateModal, setShowingBlockViewUpdateModal] =
    useState(false);
  const [showingBlockViewRevertModal, setShowingBlockViewRevertModal] =
    useState(false);
  const [showingBlockViewFeedback, setShowingBlockViewFeedback] =
    useState(false);
  const [isBlockViewFeedback, setIsBlockViewFeedback] = useState(false);

  const getIsLegacyPlanViewActive = () => {
    if (!coachUseLegacyPlanView) {
      return false;
    }
    return coachLegacyPlanViewSelection !== false;
  };

  const [isLegacyPlanViewActive, setIsLegacyPlanViewActive] = useState(
    getIsLegacyPlanViewActive(),
  );
  const { shouldShowNonLegacyBlockTutorial, shouldShowBlockViewBanner } =
    getBlockTutorialVisibility({
      isMobile,
      isLegacyCoach,
      coachUseLegacyPlanView,
      hasDismissedBlockViewBanner,
      shouldShowProgramBlockViewTutorial:
        client?.coach?.shouldShowUISectionInfo(
          UISections.PROGRAM_BLOCK_VIEW_V1,
        ),
    });
  const shouldShowProtocolsGuideBlockView = Boolean(
    client?.coach?.showingProtocolsGuideBlockView,
  );
  const shouldShowBlockDashboardSection =
    client?.coach?.shouldShowBlockDashboardSection ?? true;
  const workoutsTutorialTextKey = shouldShowBlockDashboardSection
    ? "tutorialProgramWorkouts-string"
    : "tutorialProgramWorkoutsNoDashboard-string";
  const isShowingDashboard =
    shouldShowBlockDashboardSection &&
    client?.selectedBlock?.isShowingDashboard === true;
  const shouldUseReducedBlockViewHeaderGap = shouldReduceBlockViewHeaderGap({
    isShowingDashboard,
    isLegacyPlanViewActive,
    shouldShowBlockViewBanner,
    shouldShowNonLegacyBlockTutorial,
    shouldShowProtocolsGuideBlockView,
  });
  const blockHeaderBottomMargin = getBlockHeaderBottomMargin({
    isShowingDashboard,
    shouldShowBlockViewBanner,
    shouldReduceBlockViewHeaderGap: shouldUseReducedBlockViewHeaderGap,
  });

  const [showingAddToCalendar, setShowingAddToCalendar] = useState(false);

  const [showingCopyToClient, setShowingCopyToClient] = useState(false);
  const [showingEditBlock, setShowingEditBlock] = useState(false);

  const [showingDeleteAlert, setShowingDeleteAlert] = useState(false);
  const deleteAlertTitle = "deleteBlockAlertTitle-string";
  const deleteAlertText = "deleteBlockAlertText-string";

  const deleteBlock = () => {
    if (client.selectedBlock) {
      client.deleteBlock(client.selectedBlock);
    }
    setShowingDeleteAlert(false);
  };

  useEffect(() => {
    if (shouldShowBlockViewUpdateModal) {
      setShowingBlockViewUpdateModal(true);
      if (client?.selectedBlock?.isShowingDashboard) {
        client.selectedBlock.setProperty("isShowingDashboard", false);
      }
    }
  }, [shouldShowBlockViewUpdateModal]);

  useEffect(() => {
    setIsLegacyPlanViewActive(getIsLegacyPlanViewActive());
  }, [coachUseLegacyPlanView, coachLegacyPlanViewSelection]);

  useEffect(() => {
    if (!shouldShowBlockDashboardSection && client?.selectedBlock?.isShowingDashboard) {
      client.selectedBlock.setProperty("isShowingDashboard", false);
    }
  }, [client, shouldShowBlockDashboardSection, client?.selectedBlock]);

  const blockViewUpdateBulletKeys = useMemo(
    () => [
      "blockViewUpdateBullet1-string",
      "blockViewUpdateBullet2-string",
      "blockViewUpdateBullet3-string",
      "blockViewUpdateBullet4-string",
      "blockViewUpdateBullet5-string",
    ],
    [],
  );

  const handleTryBlockView = async () => {
    if (!client?.coach) {
      setShowingBlockViewUpdateModal(false);
      return;
    }

    await client.coach.markUISectionAsShown(UISections.BLOCK_VIEW_UPDATE_V1);
    setIsLegacyPlanViewActive(false);
    if (client.coach.useLegacyPlanView === true) {
      await client.coach.setUseLegacyPlanViewSelection(false);
    }
    setShowingBlockViewUpdateModal(false);
  };

  const handleCloseBlockViewBanner = async () => {
    if (client?.coach) {
      await client.coach.markUISectionAsShown(
        UISections.BLOCK_VIEW_TUTORIAL_BANNER_V1,
      );
    }
  };

  const handleCloseNonLegacyBlockTutorial = async () => {
    if (client?.coach) {
      await client.coach.markUISectionAsShown(UISections.PROGRAM_BLOCK_VIEW_V1);
    }
  };

  const handleRevertToOldView = async () => {
    if (client?.coach) {
      await client.coach.setUseLegacyPlanView(true);
      await client.coach.setUseLegacyPlanViewSelection(true);
    }
    setIsLegacyPlanViewActive(true);
    setShowingBlockViewRevertModal(false);
  };

  const handlePlanificationTabSelect = useCallback(
    (key) => {
      if (key === "dashboard" && !shouldShowBlockDashboardSection) {
        return;
      }
      client.selectedBlock?.setProperty(
        "isShowingDashboard",
        key === "dashboard",
      );
    },
    [client.selectedBlock, shouldShowBlockDashboardSection],
  );

  const ViewModeToggle = ({ isBlockView, onToggle }) => {
    const [isBlockHovered, setIsBlockHovered] = useState(false);
    const [isOldHovered, setIsOldHovered] = useState(false);

    return (
      <View style={styles.viewModeToggle}>
        <Tooltip content="workoutsView-string" placement="top">
          <TouchableOpacity
            onPress={() => onToggle(true)}
            style={[
              styles.viewModeButton,
              isBlockView && styles.viewModeButtonActive,
              !isBlockView && isBlockHovered && styles.viewModeButtonHovered,
            ]}
            onMouseEnter={() => !isMobile && setIsBlockHovered(true)}
            onMouseLeave={() => setIsBlockHovered(false)}
          >
            <Ionicons
              name="grid-outline"
              size={16}
              color={isBlockView ? Colors.whiteColor : Colors.blackColor}
            />
          </TouchableOpacity>
        </Tooltip>
        <Tooltip content="oldView-string" placement="top">
          <TouchableOpacity
            onPress={() => onToggle(false)}
            style={[
              styles.viewModeButton,
              !isBlockView && styles.viewModeButtonActive,
              isBlockView && isOldHovered && styles.viewModeButtonHovered,
            ]}
            onMouseEnter={() => !isMobile && setIsOldHovered(true)}
            onMouseLeave={() => setIsOldHovered(false)}
          >
            <Ionicons
              name="list-outline"
              size={16}
              color={!isBlockView ? Colors.whiteColor : Colors.blackColor}
            />
          </TouchableOpacity>
        </Tooltip>
      </View>
    );
  };

  const AddToCalendarButton = observer(({ block }) => {
    const [isHovered, setIsHovered] = useState(false);
    const hasAtLeastOneWorkoutWithExercises = block.workouts.some(
      (w) => w.exercises && w.exercises.length > 0,
    );

    const allWorkoutsHaveExercises = block.workouts.every(
      (w) => w.exercises && w.exercises.length > 0,
    );

    const allWorkoutsWithDate = block.workouts.every((w) => w.date);

    const buttonContent = (
      <TouchableOpacity
        onPress={
          hasAtLeastOneWorkoutWithExercises
            ? setShowingAddToCalendar
            : undefined
        }
        disabled={!hasAtLeastOneWorkoutWithExercises}
        style={{
          flexShrink: 1,
          opacity:
            isHovered || !allWorkoutsHaveExercises || allWorkoutsWithDate
              ? 0.6
              : 1,
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View
          style={{
            height: 28,
            backgroundColor: !hasAtLeastOneWorkoutWithExercises
              ? Colors.grayColor
              : Colors.bubbleColor,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: !hasAtLeastOneWorkoutWithExercises
              ? Colors.grayColor
              : Colors.bubbleColor,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Text
            style={{
              textAlign: "center",
              textAlignVertical: "center",
              color: Colors.whiteColor,
              paddingHorizontal: 8,
              fontSize: 11,
            }}
          >
            {t("addToCalendar-string")}
          </Text>
        </View>
      </TouchableOpacity>
    );

    return !hasAtLeastOneWorkoutWithExercises ? (
      <Tooltip
        content="createWorkoutsBeforeAddingToCalendar-string"
        placement="top"
        maxWidth={250}
      >
        {buttonContent}
      </Tooltip>
    ) : (
      buttonContent
    );
  });

  const BlockTitle = observer(({ block }) => {
    const [isHovered, setIsHovered] = useState(false);
    const { showToast } = useToast();
    const blockMenuItems = useMemo(
      () => [
        {
          key: "editBlock",
          label: t("editBlock-string"),
          icon: { type: "ionicons", name: "create-outline" },
        },
        {
          key: "copyBlock",
          label: t("copyBlock-string"),
          icon: { type: "ionicons", name: "duplicate-outline" },
        },
        {
          key: "copyFullBlock",
          label: t("copyFullBlock-string"),
          icon: { type: "ionicons", name: "duplicate-outline" },
        },
        {
          key: "copyToClient",
          label: t("copyToAnotherClient-string"),
          icon: { type: "ionicons", name: "person-add-outline" },
        },
        {
          key: "exportToLibrary",
          label: t("exportToLibrary-string"),
          icon: { type: "ionicons", name: "book-outline" },
        },
        {
          key: "delete",
          label: t("delete-string"),
          icon: { type: "ionicons", name: "trash-outline" },
          isDanger: true,
        },
      ],
      [t],
    );

    const handleBlockMenuAction = useCallback(
      (itemKey) => {
        if (itemKey === "editBlock") {
          client.setSelectedBlock(block);
          setShowingEditBlock(true);
          return;
        }

        if (itemKey === "copyBlock") {
          const newBlock = client.copyBlock(
            t,
            block,
            false,
            undefined,
            showToast,
          );
          client.updateClientTransactionData(newBlock.id);
          client.startOrResetBatchTimer();
          return;
        }

        if (itemKey === "copyFullBlock") {
          const newBlock = client.copyBlock(
            t,
            block,
            true,
            undefined,
            showToast,
          );
          client.updateClientTransactionData(newBlock.id);
          client.startOrResetBatchTimer();
          return;
        }

        if (itemKey === "copyToClient") {
          setShowingCopyToClient(true);
          return;
        }

        if (itemKey === "exportToLibrary") {
          block.copyBlockToLibrary(showToast);
          return;
        }

        if (itemKey === "delete") {
          setShowingDeleteAlert(true);
        }
      },
      [block, client, showToast, t],
    );
    return (
      <View
        style={{
          width: screenSize === "small" && "100%",
          ...(screenSize === "small" ? { flex: 1 } : {}),
          flexShrink: 1,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text
            style={[Fonts.bold18, { marginRight: 12 }]}
            numberOfLines={2}
            onPress={() => {
              if (screenSize === "small") {
                if (block.client) {
                  block.client.setSelectedBlock(null);
                } else if (block.coach) {
                  block.coach.setSelectedBlock(null);
                }
              }
            }}
          >
            {block.name}
          </Text>
          <FloatingMenu
            placement="bottom-end"
            width={220}
            items={blockMenuItems}
            trigger={
              <View
                style={{ opacity: isHovered ? 0.6 : 1 }}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Entypo name="dots-three-horizontal" size={18} color="black" />
              </View>
            }
            renderItem={({ item, close }) => (
              <HoverableTouchableOpacity
                style={styles.blockMenuItemRow}
                hoverStyle={styles.blockMenuItemHover}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  handleBlockMenuAction(item.key);
                  close();
                }}
                title={item.label}
              >
                {item?.icon?.type === "ionicons" ? (
                  <Ionicons
                    name={item.icon.name}
                    size={14}
                    color={
                      item.isDanger ? Colors.redColor : Colors.darkgrayColor
                    }
                  />
                ) : null}
                <Text
                  style={[
                    styles.blockMenuItemText,
                    item.isDanger ? styles.blockMenuItemDanger : null,
                  ]}
                >
                  {item.label}
                </Text>
              </HoverableTouchableOpacity>
            )}
          />
        </View>

        <View
          style={{
            backgroundColor: Colors.blackColor,
            height: 1,
            opacity: 0.2,
            marginTop: 8,
          }}
        />
      </View>
    );
  });

  const PlanView = observer(({ block }) => {
    return (
      <View
        style={{
          flex: 1,
        }}
      >
        {block && block.selectedWorkout && (
          <>
            {block.selectedWorkout.showingEditWorkout ? (
              <View
                style={{
                  paddingBottom: 0,
                  borderRadius: 14,
                  borderWidth: 0.1,
                  borderColor: Colors.borderColor,
                }}
              >
                <EditWorkoutView
                  coach={client.coach}
                  workout={block.selectedWorkout}
                />
              </View>
            ) : (
              <View
                style={{
                  paddingBottom: 0,
                  borderRadius: 14,
                  borderWidth: 0.1,
                  borderColor: Colors.borderColor,
                }}
              >
                <WorkoutDescriptionView workout={block.selectedWorkout} />
              </View>
            )}
          </>
        )}
      </View>
    );
  });

  return (
    <View
      style={{
        flex: 1, //En principi no ha passat res per deixarlo. Assegurar
        justifyContent: "space-between",
        marginTop: screenSize === "small" ? 16 : 24,
      }}
    >
      {!client.selectedBlock ? (
        <BlocksList client={client} />
      ) : (
        <View
          style={{
            width: "100%",
          }}
        >
          {screenSize != "small" && (
            <View style={{ marginBottom: 12 }}>
              <BackButton
                action={() => {
                  client.setSelectedBlock(null);
                }}
                backString={t("blocks-string")}
              />
            </View>
          )}

          <View
            style={{
              width: "100%",
              flexDirection: screenSize !== "small" && "row",
              marginBottom: blockHeaderBottomMargin,
              alignItems: "center",
              gap: screenSize != "small" ? 16 : 0,
            }}
          >
            <BlockTitle block={client.selectedBlock} />
            {screenSize != "small" && <View style={{ flex: 1 }} />}
            <View
              style={{
                width: screenSize === "small" && "100%",
                flexDirection: "row",
                marginTop: screenSize === "small" && 16,
                alignItems: "center",
                gap: 16,
                justifyContent:
                  screenSize === "small" ? "space-between" : "flex-end",
              }}
            >
              <AddToCalendarButton block={client.selectedBlock} />
              {coachUseLegacyPlanView &&
                !isShowingDashboard && (
                  <ViewModeToggle
                    isBlockView={!isLegacyPlanViewActive}
                    onToggle={(useBlockView) => {
                      const nextIsLegacyPlanViewActive = !useBlockView;
                      setIsLegacyPlanViewActive(nextIsLegacyPlanViewActive);
                      if (client?.coach?.useLegacyPlanView === true) {
                        client.coach.setUseLegacyPlanViewSelection(
                          nextIsLegacyPlanViewActive,
                        );
                      }
                    }}
                  />
                )}
              {shouldShowBlockDashboardSection && (
                <View
                  style={[
                    GlobalStyles.HStack,
                    {
                      justifyContent: "space-evenly",
                      flexDirection: "row",
                    },
                  ]}
                >
                  <WorkoutsDashboardTabBar
                    selectedKey={isShowingDashboard ? "dashboard" : "workouts"}
                    onSelect={handlePlanificationTabSelect}
                    workoutsLabel={t("workouts-string")}
                    dashboardLabel={t("dashboard-string")}
                    itemGap={12}
                  />
                </View>
              )}
            </View>
          </View>

          {isShowingDashboard ? (
            <>
              {client?.coach?.shouldShowUISectionInfo(
                UISections.PROGRAM_DASHBOARD_V1,
              ) &&
                !client.coach.hideBlockDashboard && (
                  <TutorialLinkSection
                    text="tutorialProgramDashboard-string"
                    urlPath="/program/create-block-dashboard"
                    marginTop={8}
                    marginBottom={16}
                    showCloseButton={true}
                    onClose={async () =>
                      await client.coach.markUISectionAsShown(
                        UISections.PROGRAM_DASHBOARD_V1,
                      )
                    }
                  />
                )}
              <BlockDashboard block={client.selectedBlock} />
            </>
          ) : (
            <>
              {!isLegacyPlanViewActive ? (
                <BlockView
                  block={client.selectedBlock}
                  showTutorialLink={shouldShowNonLegacyBlockTutorial}
                  showTutorialBanner={shouldShowBlockViewBanner}
                  tutorialUrlPath={blockViewTutorialUrlPath}
                  onSendFeedback={() => {
                    setIsBlockViewFeedback(true);
                    setShowingBlockViewFeedback(true);
                  }}
                  onCloseTutorialLink={handleCloseNonLegacyBlockTutorial}
                  onCloseTutorialBanner={handleCloseBlockViewBanner}
                  onAccessOld={
                    isLegacyCoach
                      ? () => setShowingBlockViewRevertModal(true)
                      : undefined
                  }
                  tutorialTextKey={workoutsTutorialTextKey}
                />
              ) : (
                <>
                  {/* {shouldShowWorkoutsTutorial && (
                    <TutorialLinkSection
                      text="tutorialProgramWorkouts-string"
                      urlPath="/program/create-workout"
                      marginTop={8}
                      marginBottom={16}
                      showCloseButton={true}
                      onClose={async () =>
                        await client.coach.markUISectionAsShown(
                          UISections.PROGRAM_WORKOUTS_V1
                        )
                      }
                    />
                  )} */}
                  {isLegacyPlanViewActive && (
                    <Text style={styles.legacyPlanNotice}>
                      {t("legacyPlanViewTemporaryNotice-string")}
                    </Text>
                  )}
                  <View
                    style={{
                      flexDirection: screenSize === "large" ? "row" : "column",
                      gap: screenSize === "small" ? 8 : 24,
                      width: "100%",
                    }}
                  >
                    <View
                      style={{
                        width: screenSize === "large" ? "20%" : "100%",
                      }}
                    >
                      <BlockDescriptionView block={client.selectedBlock} />
                    </View>
                    {screenSize === "small" ? (
                      <>
                        {client.selectedBlock.selectedWorkout &&
                        (client.selectedBlock.selectedWorkout
                          .selectedExercise ||
                          client.selectedBlock.selectedWorkout
                            ?.selectedExerciseToAdd) &&
                        client.showingInfoView ? (
                          <View style={{ flex: 1 }}>
                            {/* <View style={{ marginBottom: 8 }}>
                        <BackButton
                          action={() =>
                            client.setProperty("showingInfoView", false)
                          }
                          backString={t("back-string")}
                        />
                      </View> */}
                            <View
                              style={{
                                width: "100%",
                              }}
                            >
                              <InfoView
                                client={client}
                                exercise={
                                  client.selectedBlock.selectedWorkout
                                    ?.showingEditWorkout
                                    ? client.selectedBlock.selectedWorkout
                                        ?.selectedExerciseToAdd
                                    : client.selectedBlock.selectedWorkout
                                        ?.selectedExercise
                                }
                              />
                            </View>
                          </View>
                        ) : (
                          <PlanView block={client.selectedBlock} />
                        )}
                      </>
                    ) : (
                      <>
                        <View
                          style={{
                            flexDirection: "row",
                            flex: 1,
                            gap: 24,
                          }}
                        >
                          <PlanView block={client.selectedBlock} />
                          <View
                            style={{
                              width: screenSize === "medium" ? "35%" : "32%",
                            }}
                          >
                            <InfoView
                              client={client}
                              exercise={
                                client.selectedBlock.selectedWorkout
                                  ?.showingEditWorkout
                                  ? client.selectedBlock.selectedWorkout
                                      ?.selectedExerciseToAdd
                                  : client.selectedBlock.selectedWorkout
                                      ?.selectedExercise
                              }
                            />
                          </View>
                        </View>
                      </>
                    )}
                  </View>
                </>
              )}
            </>
          )}

          {showingAddToCalendar && client.selectedBlock && (
            <AddToCalendarView
              client={client}
              isVisible={showingAddToCalendar}
              onClose={() => setShowingAddToCalendar(false)}
              block={client.selectedBlock}
            />
          )}

          {showingDeleteAlert && client.selectedBlock && (
            <DeleteBlockView
              isVisible={showingDeleteAlert}
              onClose={() => setShowingDeleteAlert(false)}
              title={deleteAlertTitle}
              text={deleteAlertText}
              action={deleteBlock}
              nameOfBlock={
                client.selectedBlock ? client.selectedBlock.name : ""
              }
            />
          )}

          {showingCopyToClient && client.selectedBlock && (
            <CopyBlockToClient
              isVisible={showingCopyToClient}
              onClose={() => setShowingCopyToClient(false)}
              block={client.selectedBlock}
            />
          )}

          {showingEditBlock && client.selectedBlock && (
            <AddBlockView
              isVisible={showingEditBlock}
              onClose={() => setShowingEditBlock(false)}
              block={client.selectedBlock}
              client={client}
            />
          )}

          {showingBlockViewUpdateModal && (
            <BlockViewUpdateModal
              isVisible={showingBlockViewUpdateModal}
              onTryItOut={handleTryBlockView}
              titleKey="blockViewUpdateTitle-string"
              bulletKeys={blockViewUpdateBulletKeys}
              buttonKey="blockViewUpdateButton-string"
            />
          )}

          {showingBlockViewRevertModal && (
            <BlockViewRevertModal
              isVisible={showingBlockViewRevertModal}
              onClose={() => setShowingBlockViewRevertModal(false)}
              onRevert={handleRevertToOldView}
              coach={client?.coach}
            />
          )}

          {showingBlockViewFeedback && (
            <SendFeedback
              isVisible={showingBlockViewFeedback}
              onClose={() => {
                setShowingBlockViewFeedback(false);
                setIsBlockViewFeedback(false);
              }}
              coach={client?.coach}
              titleKey={
                isBlockViewFeedback
                  ? "blockViewFeedbackTitle-string"
                  : undefined
              }
              textKey={
                isBlockViewFeedback ? "blockViewFeedbackText-string" : undefined
              }
              placeholderKey={
                isBlockViewFeedback
                  ? "blockViewFeedbackPlaceholder-string"
                  : undefined
              }
              source={isBlockViewFeedback ? "tutorial_banner" : null}
            />
          )}
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  // container: {
  //   flex: 1,
  //   backgroundColor: "#EFF4F5",
  //   paddingTop: 30,
  //   marginBottom: 20,
  // },
  // title: {
  //   fontSize: 14,
  //   fontWeight: "500",
  // },
  // statisticLabel: {
  //   fontSize: 13,
  // },
  // statisticValue: {
  //   flex: 1,
  //   fontSize: 13,
  //   fontWeight: "500",
  //   marginLeft: 5,
  // },
  viewModeToggle: {
    flexDirection: "row",
    backgroundColor: Colors.whiteColor,
    borderRadius: 10,
    padding: 2,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    height: 30,
    alignItems: "center",
    gap: 4,
  },
  viewModeButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 8,
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  viewModeButtonActive: {
    backgroundColor: Colors.bubbleColor,
  },
  viewModeButtonHovered: {
    backgroundColor: Colors.backgroundColor,
  },
  viewModeText: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  viewModeTextActive: {
    color: Colors.whiteColor,
  },
  legacyPlanNotice: {
    fontSize: 12,
    color: Colors.darkgrayColor,
    marginTop: 8,
    marginBottom: 12,
  },
  blockMenuItemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  blockMenuItemHover: {
    backgroundColor: Colors.hoveredGray,
  },
  blockMenuItemText: {
    fontSize: 12,
    color: Colors.blackColor,
    fontWeight: "500",
  },
  blockMenuItemDanger: {
    color: Colors.redColor,
  },
  // textInputStyle: { //Sutilitzava al Dashboard, ja no cal
  //   width: "40%",
  //   height: 40,
  //   borderColor: Colors.borderColor,
  //   backgroundColor: Colors.whiteColor,
  //   color: Colors.blackColor,
  //   borderWidth: 0.1,
  //   padding: 10,
  //   borderRadius: 10,
  //   outlineStyle: "none",
  // },
  // AttributeButton: { //Sutilitzava al Dashboard, ja no cal
  //   borderRadius: 8,
  //   borderWidth: 0.5,
  //   borderColor: Colors.borderColor,
  // },
});

export default ClientPlanification;
